import { Fragment } from 'react';
import { Typography, ListItemText, Chip } from '@material-ui/core';
import api from 'services/api';
import baseApi from 'utils/baseApi';
import { toQueryString } from 'utils/query';
import auth from 'services/auth';
import userService from 'services/data/user';

import { clearingHouseName } from 'pages/organization/organization/clearinghouse/clearinghouse.store';

export function insurerFields({ incDefaults, excludeSaved, allowAllClearing, hideInfo, clearing, filterByCodes } = {}) {
  return {
    loadOptions: search => findInsurers(search, { incDefaults, excludeSaved, allowAllClearing, hideInfo, clearing, filterByCodes }),
    rowHeight: hideInfo ? 48 : 72,
    renderItem: i => renderInsurerItem(i, { hideInfo })
  };
};

export async function findInsurers(q, { incDefaults, excludeSaved, allowAllClearing, hideInfo, clearing: clearingFilter, filterByCodes } = {}) {
  if (!q) { return { options: [] }; }

  let showSaved = auth.isClinician;

  if (clearingFilter && !Array.isArray(clearingFilter)) { clearingFilter = [ clearingFilter ]; }
  const clearing = clearingFilter
    || (allowAllClearing ? null : (await userService.clearinghouse.waitForValue())?.clearing.filter(c => c.enabled).map(c => c.clearing));

  const hasKalix = clearing === null || clearing.includes('kalix');

  // We might have the setting to not show default insurers
  const onlySavedPromise = auth.isUsBilling && !excludeSaved ? userService.insurerSettings.waitForValue() : Promise.resolve({ useOnlySaved: false });
  const defaultValuesPromise = onlySavedPromise
    .then(({ useOnlySaved }) => {
      showSaved = auth.isClinician && !useOnlySaved;
      return Promise.all([
        useOnlySaved || !hasKalix ? Promise.resolve({ data: [] }) : api.get(`clearinghouse/kalix/payers?${toQueryString({ search: q })}`),
        baseApi.get(`invoicepayer?${toQueryString({
          q,
          incDefaults: !!incDefaults,
          countryCode: auth.user.country,
          onlySaved: useOnlySaved ? true : undefined,
          clearing,
          combine: hideInfo ? true : undefined
        })}`)
      ]);
    });

  const savedPromised = excludeSaved ? Promise.resolve() : api.get(`invoices/insurers?${toQueryString({ search: q, clearing })}`);

  const res = await Promise.all([ savedPromised, defaultValuesPromise ]);

  const savedItems = excludeSaved
    ? []
    : res[0].data.results
      .map(d => {
        d.isSaved = true;
        d.desc = d.description;
        delete d.description;
        return d;
      })
      .filter(d => filterByCodes ? !filterByCodes.includes(d.code) : true);

  const kalixItems = res[1][0].data
    .map(d => {
      d.isSaved = false;
      return d;
    })
    .filter(i => filterByCodes ? !filterByCodes.includes(i.code) : true)
    .filter(i => !savedItems.some(s => (hideInfo || i.clearing === 'kalix') && s.name === i.name && s.code === i.code));

  const allItems = res[1][1].data
    .map(d => {
      d.isSaved = false;
      return d;
    })
    .filter(i => filterByCodes ? !filterByCodes.includes(i.code) : true)
    .filter(i => !savedItems.some(s => (hideInfo || s.clearing === i.clearing) && s.name === i.name && s.code === i.code))
    .filter(i => !kalixItems.some(s => (hideInfo || s.clearing === 'kalix') && s.code === i.code));

  return {
    options: (savedItems)
      .concat(kalixItems)
      .concat(allItems)
      .map(i => ({
        value: `${i.code || ''}:${i.name}`,
        name: `${i.code ? i.code + ': ' : ''}${i.name}${(showSaved && i.isSaved ? ' (Saved)' : '')}`,
        data: i
      }))
  };
}

export function renderInsurerItem(item, { hideInfo } = {}) {
  const { name, data } = item;
  return <Fragment>
    <ListItemText
      disableTypography
      primary={
        <Typography variant="subtitle1" display="block" noWrap>
          { name }
        </Typography>
      }
      secondary={
        auth.isUsBilling && !hideInfo &&
          <Typography variant="caption" display="block" noWrap>
            { clearingHouseName[data.clearing] }
          </Typography>
      }
    />
    { auth.isUsBilling && !hideInfo && data.isParticipating != null &&
      <Chip label={ data.isParticipating ? 'Participating' : 'Non-Participating' } />
    }
  </Fragment>;
}

export async function findPayerNames(payerIds, clearing) {
  const res = await baseApi.post(`invoicepayer/names?clearing=${clearing || 'kalix'}`, { payerIds });
  return new Map(res.data);
}